import { useEffect, useState } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { useAuth } from '../components/auth/AuthContext';

export function usePlatformOwner() {
  const { currentUser, impersonatedUser } = useAuth();
  const contextUid = currentUser?.uid;
  const isImpersonating = Boolean(impersonatedUser);
  const [state, setState] = useState({ contextUid: null, firebaseUser: null, isOwner: false, loading: true, error: null });

  useEffect(() => {
    let active = true;
    let revision = 0;
    if (isImpersonating) {
      setState({ contextUid, firebaseUser: null, isOwner: false, loading: false, error: null });
      return undefined;
    }
    setState({ contextUid, firebaseUser: null, isOwner: false, loading: Boolean(auth.currentUser), error: null });

    const unsubscribe = onIdTokenChanged(auth, async firebaseUser => {
      if (!active) return;
      const currentRevision = ++revision;
      setState({ contextUid, firebaseUser: null, isOwner: false, loading: true, error: null });
      if (!firebaseUser || firebaseUser !== auth.currentUser) {
        setState({ contextUid, firebaseUser: null, isOwner: false, loading: false, error: null });
        return;
      }
      try {
        // Never read claims from the Firestore-enriched/impersonated user object.
        const token = await firebaseUser.getIdTokenResult();
        if (active && currentRevision === revision) {
          setState({
            contextUid,
            firebaseUser,
            isOwner: auth.currentUser === firebaseUser && token.claims.platformOwner === true,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        if (active && currentRevision === revision) {
          setState({ contextUid, firebaseUser: null, isOwner: false, loading: false, error });
        }
      }
    }, error => {
      revision++;
      if (active) setState({ contextUid, firebaseUser: null, isOwner: false, loading: false, error });
    });

    return () => {
      active = false;
      revision++;
      unsubscribe();
    };
  }, [contextUid, isImpersonating]);

  if (isImpersonating) return { isOwner: false, loading: false, error: null };
  if (state.contextUid !== contextUid) return { isOwner: false, loading: Boolean(auth.currentUser), error: null };
  return {
    isOwner: state.isOwner && state.firebaseUser === auth.currentUser,
    loading: state.loading,
    error: state.error
  };
}
